import type { Work, WorkType } from "@/types/work";

export type SortKey = "latest" | "popular" | "rating";

export const ALL_GENRES = [
  "爱情伦理",
  "社会伦理",
  "心理伦理",
  "家庭伦理",
  "悬疑伦理",
  "都市伦理",
  "治愈伦理",
];

export function getWorkById(works: Work[], id: string): Work | undefined {
  return works.find((item) => item.id === id);
}

export function getLatest(works: Work[], type: WorkType, limit = 4): Work[] {
  return works
    .filter((item) => item.type === type)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .slice(0, limit);
}

export function getFeatured(works: Work[], limit = 5): Work[] {
  return works
    .filter((item) => Boolean(item.backdrop))
    .sort((a, b) => b.views - a.views)
    .slice(0, limit);
}

export function getRelated(works: Work[], current: Work, limit = 5): Work[] {
  return works
    .filter((item) => item.id !== current.id && item.type === current.type)
    .map((item) => ({
      item,
      score:
        item.genres.filter((g) => current.genres.includes(g)).length * 10 +
        item.tags.filter((t) => current.tags.includes(t)).length,
    }))
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function sortWorks(works: Work[], sort: SortKey): Work[] {
  const copy = [...works];
  if (sort === "popular") return copy.sort((a, b) => b.views - a.views);
  if (sort === "rating") return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

export interface FilterOptions {
  genres: string[];
  years: number[];
  ratingMin: number | null;
}

export function filterWorks(
  works: Work[],
  { genres, years, ratingMin }: FilterOptions,
): Work[] {
  return works.filter((item) => {
    if (genres.length > 0 && !item.genres.some((g) => genres.includes(g))) {
      return false;
    }
    if (years.length > 0 && !years.includes(item.year)) return false;
    if (ratingMin !== null && item.rating < ratingMin) return false;
    return true;
  });
}

export function searchWorks(works: Work[], keyword: string): Work[] {
  const q = keyword.trim();
  if (!q) return [];
  const lower = q.toLowerCase();
  return works.filter((item) => {
    if (item.title.toLowerCase().includes(lower)) return true;
    if (item.synopsis.some((p) => p.toLowerCase().includes(lower))) return true;
    if (item.genres.some((g) => g.toLowerCase().includes(lower))) return true;
    if (item.tags.some((t) => t.toLowerCase().includes(lower))) return true;
    if (item.director.toLowerCase().includes(lower)) return true;
    return false;
  });
}

export function collectYears(works: Work[], type?: WorkType): number[] {
  const pool = type ? works.filter((w) => w.type === type) : works;
  return Array.from(new Set(pool.map((w) => w.year))).sort((a, b) => b - a);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
}

export function paginate<T>(items: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    pageItems: items.slice(start, start + pageSize),
    totalPages,
    currentPage: safePage,
  };
}